import numeral from "numeral";
import {
  timeDifference,
  ageCalculator,
  FormatStatus,
  ageYearCalculator,
  calculateHours,
  timeDifferenceFull,
  timeDiffHour,
  timeDiffHourandMinutes,
} from "../../components/functions/Functions";
import { diffTime, formatLimb } from "../../utils/formatFunctionForExport";

/**
 * Helper: coerce to real Number for Excel; returns null for blanks/invalid
 */
export const toNumber = (val) => {
  if (val === null || val === undefined) return null;
  const raw =
    typeof val === "string" ? val.trim().replace(/[^0-9.-]/g, "") : val;
  const n = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Format date to Buddhist Era (BE) format
 */
export const formatDateBE = (input) => {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const yearBE = d.getFullYear() + 543;
  return `${day}/${month}/${yearBE}`;
};

/**
 * Combine paper date and paper time
 */
export const paperDateNpaperTime = (paperDate, paperTime) => {
  const useDate = paperDate + "T" + paperTime + ":00.000Z";
  return useDate;
};

/**
 * Format phone numbers from more_patient array
 */
const formatPhone = (phone) => {
  if (phone) {
    const myString = phone.join(",");
    const useMyString = myString.replace(",", "");
    return useMyString;
  }
  return "";
};

/**
 * Format relation from more_patient array
 */
const formatRelation = (relation) => {
  if (relation) {
    const myString = relation.join(",");
    const useMyString = myString.replace(",", "");
    return useMyString;
  }
  return "";
};

/**
 * Process attempts data from task (ใช้ข้อมูลที่ได้จาก API แทนการเรียก API ใหม่)
 */
const processAttempts = (thisAttempts) => {
  // Count of attempts
  const count_of_attempts = thisAttempts?.length || 0;

  // Total time in room
  let thisTimeImRoom = [];
  thisAttempts?.forEach((row) => {
    if (row?.room_in && row?.out_of_room) {
      const total_time = diffTime(row?.room_in, row?.out_of_room);
      thisTimeImRoom.push(total_time);
    }
  });
  const total_time_room = Math.round(
    thisTimeImRoom?.reduce((acc, curr) => acc + curr, 0)
  );

  // Total all time
  let thisAllTime = 0;
  if (thisAttempts?.length > 0) {
    if (
      thisAttempts[0]?.room_in &&
      thisAttempts[thisAttempts.length - 1]?.out_of_room
    ) {
      thisAllTime = diffTime(
        thisAttempts[0].room_in,
        thisAttempts[thisAttempts.length - 1].out_of_room
      );
    }
  }
  const total_all_time = Math.round(thisAllTime);

  // Doctor from successful attempts
  const successfulAttempts = thisAttempts?.filter(
    (a) => a?.status === "Successful"
  );
  let keepthisDoctornStr = "";
  successfulAttempts?.forEach((a) => {
    if (a?.attending_name?.label) {
      keepthisDoctornStr += a?.attending_name?.label;
    }
  });

  // Time calculations
  let thisTime = [];
  thisAttempts?.forEach((a, i) => {
    if (a?.room_in && a?.out_of_room) {
      const diff_time =
        Math.abs(
          new Date().setHours(
            a?.room_in?.split(":")[0],
            a?.room_in?.split(":")[1]
          ) -
            new Date().setHours(
              a?.out_of_room?.split(":")[0],
              a?.out_of_room?.split(":")[1]
            )
        ) / 60000;
      const result =
        numeral(diff_time).format("0") > 0 ? numeral(diff_time).format("0") : 0;
      thisTime.push({ name: result, round: i + 1 });
    }
  });
  let keepthisTimeStr = "";
  thisTime.forEach((row) => {
    keepthisTimeStr += `${row?.round})${row?.name}mins|`;
  });

  // Complications
  let thisComplication = [];
  thisAttempts?.forEach((a, i) => {
    if (a?.complications_pneumothorax)
      thisComplication.push({ name: "Pneumothorax", round: i + 1 });
    if (a?.complications_hemothorax)
      thisComplication.push({ name: "Hemothorax", round: i + 1 });
    if (a?.complications_arterial_puncture)
      thisComplication.push({ name: "Arterial puncture", round: i + 1 });
    if (a?.complications_paresthesia)
      thisComplication.push({ name: "Paresthesia", round: i + 1 });
    if (a?.complications_nerve_injury)
      thisComplication.push({ name: "Nerve injury", round: i + 1 });
    if (a?.complications_limb_ischemia_related_to_arterial_puncture)
      thisComplication.push({
        name: "Limb ischemia related to arterial puncture",
        round: i + 1,
      });
    if (a?.other_complications)
      thisComplication.push({
        name: a?.other_complications_name,
        round: i + 1,
      });
  });
  let keepthisComplicationStr = "";
  thisComplication.forEach((row) => {
    keepthisComplicationStr += `${row?.round}.${row?.name}|`;
  });

  return {
    count_of_attempts,
    total_time_room,
    total_all_time,
    use_doctor: keepthisDoctornStr,
    time: keepthisTimeStr,
    complications: keepthisComplicationStr,
  };
};

/**
 * Process follows data from task
 */
const processFollows = (thisFollows) => {
  // By (phone/actual visit)
  let thisBy = [];
  thisFollows?.forEach((a, i) => {
    if (a?.by_phone) thisBy.push({ name: "phone", round: i + 1 });
    if (a?.by_actial_visit) thisBy.push({ name: "Actual visit", round: i + 1 });
  });
  let keepthisBynStr = "";
  thisBy.forEach((row) => {
    keepthisBynStr += `${row?.round}.${row?.name}|`;
  });

  // Line Patency
  let thisLinePatency = [];
  thisFollows?.forEach((a, i) => {
    if (a?.line_ok_all) thisLinePatency.push({ name: "OK ALL", round: i + 1 });
    if (a?.line_wo_port)
      thisLinePatency.push({ name: "WO Port", round: i + 1 });
    if (a?.line_wo_distal)
      thisLinePatency.push({ name: "Distal", round: i + 1 });
    if (a?.line_wo_proximal)
      thisLinePatency.push({ name: "Proximal", round: i + 1 });
    if (a?.line_wo_purple)
      thisLinePatency.push({ name: "Purple", round: i + 1 });
    if (a?.line_wo_white) thisLinePatency.push({ name: "White", round: i + 1 });
    if (a?.other_line_wo_port)
      thisLinePatency.push({ name: a?.other_line_wo_port_name, round: i + 1 });
    if (a?.line_wo_observe)
      thisLinePatency.push({ name: "Observe", round: i + 1 });
    if (a?.line_wo_rtpax)
      thisLinePatency.push({ name: "rtPA x", round: i + 1 });
    if (a?.line_wo_result_opened)
      thisLinePatency.push({ name: "Opened", round: i + 1 });
    if (a?.line_wo_result_wo)
      thisLinePatency.push({ name: "WO", round: i + 1 });
    if (a?.line_wo_result_co)
      thisLinePatency.push({ name: "CO", round: i + 1 });
    // CO
    if (a?.line_co_port)
      thisLinePatency.push({ name: "CO Port", round: i + 1 });
    if (a?.line_co_distal)
      thisLinePatency.push({ name: "Distal", round: i + 1 });
    if (a?.line_co_proximal)
      thisLinePatency.push({ name: "Proximal", round: i + 1 });
    if (a?.line_co_purple)
      thisLinePatency.push({ name: "Purple", round: i + 1 });
    if (a?.line_co_white) thisLinePatency.push({ name: "White", round: i + 1 });
    if (a?.other_line_co_port)
      thisLinePatency.push({ name: a?.other_line_wo_port_name, round: i + 1 });
    if (a?.line_co_observe)
      thisLinePatency.push({ name: "Observe", round: i + 1 });
    if (a?.line_co_rtpax)
      thisLinePatency.push({ name: "rtPA x", round: i + 1 });
    if (a?.line_co_result_opened)
      thisLinePatency.push({ name: "Opened", round: i + 1 });
    if (a?.line_co_result_wo)
      thisLinePatency.push({ name: "WO", round: i + 1 });
    if (a?.line_co_result_co)
      thisLinePatency.push({ name: "CO", round: i + 1 });
  });
  let keepLinePatStr = "";
  thisLinePatency.forEach((row) => {
    keepLinePatStr += `${row?.round}.${row?.name}|`;
  });

  // Line Infection
  let thisLineIn = [];
  thisFollows?.forEach((a, i) => {
    if (a?.line_infection_nil) thisLineIn.push({ name: "Nil", round: i + 1 });
    if (a?.line_infection_bug_found)
      thisLineIn.push({ name: "Bug Found", round: i + 1 });
    if (a?.line_infection_bug_found_cs_plus)
      thisLineIn.push({ name: "C/S {+} ve", round: i + 1 });
    if (a?.line_infection_bug_found_distal)
      thisLineIn.push({ name: "Distal", round: i + 1 });
    if (a?.line_infection_bug_found_proximal)
      thisLineIn.push({ name: "Proximal", round: i + 1 });
    if (a?.line_infection_bug_found_purple)
      thisLineIn.push({ name: "Purple", round: i + 1 });
    if (a?.line_infection_bug_found_white)
      thisLineIn.push({ name: "White", round: i + 1 });
    if (a?.other_line_infection_bug_found)
      thisLineIn.push({
        name: a?.other_line_infection_bug_found_name,
        round: i + 1,
      });
    if (a?.line_infection_bug_found_organism)
      thisLineIn.push({
        name: a?.line_infection_bug_found_organism,
        round: i + 1,
      });
  });
  let keepthisLineInStr = "";
  thisLineIn.forEach((row) => {
    keepthisLineInStr += `${row?.round}.${row?.name}|`;
  });

  // Line Displacement
  let thisLineDis = [];
  thisFollows?.forEach((a, i) => {
    if (a?.line_disledge_nil) thisLineDis.push({ name: "Nil", round: i + 1 });
    if (a?.line_disledge_new_exit_mark_at)
      thisLineDis.push({
        name: a?.line_disledge_new_exit_mark_at_name,
        round: i + 1,
      });
    if (a?.line_disledge_indwellend_name)
      thisLineDis.push({
        name: new Date(a?.line_disledge_indwellend_name),
        round: i + 1,
      });
    if (a?.complete_dislodge)
      thisLineDis.push({ name: "Completely dislodged", round: i + 1 });
  });
  let keepthisLinDisnStr = "";
  thisLineDis.forEach((row) => {
    keepthisLinDisnStr += `${row?.round}.${row?.name}|`;
  });

  // Catheter Related
  let thisCatRe = [];
  thisFollows?.forEach((a, i) => {
    if (a?.catheter_related_limb_edema_no)
      thisCatRe.push({ name: "no", round: i + 1 });
    if (a?.catheter_related_limb_edema_yes)
      thisCatRe.push({ name: "yes", round: i + 1 });
    if (a?.catheter_related_limb_edema_date)
      thisCatRe.push({
        name: new Date(a?.catheter_related_limb_edema_date).toLocaleDateString(
          "th-TH"
        ),
        round: i + 1,
      });
    if (a?.remark) thisCatRe.push({ name: a?.remark, round: i + 1 });
  });
  let keepthisCatReStr = "";
  thisCatRe.forEach((row) => {
    keepthisCatReStr += `${row?.round}.${row?.name}|`;
  });

  // DVT
  let thisDVT = [];
  thisFollows?.forEach((a, i) => {
    if (a?.presence_of_dvt_no) thisDVT.push({ name: "no", round: i + 1 });
    if (a?.presence_of_dvt_yes) thisDVT.push({ name: "yes", round: i + 1 });
    if (a?.presence_of_dvt_na) thisDVT.push({ name: "NA", round: i + 1 });
  });
  let keepthisDVTStr = "";
  thisDVT.forEach((row) => {
    keepthisDVTStr += `${row?.round}.${row?.name}|`;
  });

  // Management
  let thisManagement = [];
  thisFollows?.forEach((a, i) => {
    if (a?.management_observation)
      thisManagement.push({ name: "Observation", round: i + 1 });
    if (a?.management_anticoagulation)
      thisManagement.push({
        name: a?.management_anticoagulation_name,
        round: i + 1,
      });
    if (a?.management_catheter_removal)
      thisManagement.push({ name: "Catheter removal", round: i + 1 });
  });
  let keepthisManaStr = "";
  thisManagement.forEach((row) => {
    keepthisManaStr += `${row?.round}.${row?.name}|`;
  });

  // Other
  let thisOther = [];
  thisFollows?.forEach((a, i) => {
    if (a?.Others) thisOther.push({ name: "Others", round: i + 1 });
    if (a?.line_disledge_breakage_leak_port)
      thisOther.push({ name: "Breakage/leak port", round: i + 1 });
    if (a?.line_disledge_distal)
      thisOther.push({ name: "Distal", round: i + 1 });
    if (a?.line_disledge_proximal)
      thisOther.push({ name: "Proximal", round: i + 1 });
    if (a?.line_disledge_purple)
      thisOther.push({ name: "Purple", round: i + 1 });
    if (a?.line_disledge_red) thisOther.push({ name: "Red", round: i + 1 });
    if (a?.other_line_disledge)
      thisOther.push({ name: a?.other_line_disledge_name, round: i + 1 });
    if (a?.line_retained_fb_port)
      thisOther.push({ name: "Retained FB Port", round: i + 1 });
    if (a?.line_retained_fb_distal)
      thisOther.push({ name: "Distal", round: i + 1 });
    if (a?.line_retained_fb_proximal)
      thisOther.push({ name: "Proximal", round: i + 1 });
    if (a?.line_retained_fb_purple)
      thisOther.push({ name: "Purple", round: i + 1 });
    if (a?.line_retained_fb_red) thisOther.push({ name: "Red", round: i + 1 });
    if (a?.other_line_retained_fb)
      thisOther.push({ name: a?.other_line_retained_fb_name, round: i + 1 });
  });
  let keepthisOtherStr = "";
  thisOther.forEach((row) => {
    keepthisOtherStr += `${row?.round}.${row?.name}|`;
  });

  // Exit Site Infection
  let thisInfection = [];
  thisFollows?.forEach((a, i) => {
    if (a?.exit_site_infection)
      thisInfection.push({ name: a?.exit_site_infection_name, round: i + 1 });
  });
  let keepthisInfecStr = "";
  thisInfection.forEach((row) => {
    keepthisInfecStr += `${row?.round}.${row?.name}|`;
  });

  // Find LATEST follow with line_is_no_loanger
  // (กรณี re-summarize หลังถูกดึงกลับ จะมี follow เก่าค้างอยู่ ต้องเอาตัวล่าสุด)
  const followsWithRemoval = (thisFollows || []).filter(
    (row) => !!row?.line_is_no_loanger
  );
  const findFollowTotal =
    followsWithRemoval.length > 0
      ? followsWithRemoval[followsWithRemoval.length - 1]
      : undefined;

  // Any_* boolean flags (1 = yes, "" = no)
  const any_occlusion = thisFollows?.some(
    (a) =>
      a?.line_wo_port || a?.line_wo_distal || a?.line_wo_proximal ||
      a?.line_wo_purple || a?.line_wo_white ||
      a?.line_co_port || a?.line_co_distal || a?.line_co_proximal ||
      a?.line_co_purple || a?.line_co_white
  ) ? 1 : "";

  const any_wo = thisFollows?.some(
    (a) =>
      a?.line_wo_port || a?.line_wo_distal || a?.line_wo_proximal ||
      a?.line_wo_purple || a?.line_wo_white
  ) ? 1 : "";

  const any_co = thisFollows?.some(
    (a) =>
      a?.line_co_port || a?.line_co_distal || a?.line_co_proximal ||
      a?.line_co_purple || a?.line_co_white
  ) ? 1 : "";

  const any_rtpa = thisFollows?.some(
    (a) => a?.line_wo_rtpax || a?.line_co_rtpax
  ) ? 1 : "";

  const any_infection = thisFollows?.some(
    (a) => a?.line_infection_bug_found
  ) ? 1 : "";

  const any_displacement = thisFollows?.some(
    (a) => a?.line_disledge_new_exit_mark_at || a?.complete_dislodge
  ) ? 1 : "";

  const any_limb_edema = thisFollows?.some(
    (a) => a?.catheter_related_limb_edema_yes
  ) ? 1 : "";

  const any_dvt = thisFollows?.some(
    (a) => a?.presence_of_dvt_yes
  ) ? 1 : "";

  const any_breakage = thisFollows?.some(
    (a) => a?.line_disledge_breakage_leak_port
  ) ? 1 : "";

  const any_retained_fb = thisFollows?.some(
    (a) =>
      a?.line_retained_fb_port || a?.line_retained_fb_distal ||
      a?.line_retained_fb_proximal || a?.line_retained_fb_purple ||
      a?.line_retained_fb_red
  ) ? 1 : "";

  const any_limb_ischemia = thisFollows?.some(
    (a) => a?.line_limb_ischemia_related_to_arterial_puncture
  ) ? 1 : "";

  const any_nerve_injury = thisFollows?.some(
    (a) => a?.line_nerve_injury_weakness_or_paresthesia
  ) ? 1 : "";

  const any_exit_site_infection = thisFollows?.some(
    (a) => a?.exit_site_infection
  ) ? 1 : "";

  return {
    by: keepthisBynStr,
    line_patency: keepLinePatStr,
    line_infection: keepthisLineInStr,
    line_displacement: keepthisLinDisnStr,
    catheter_related: keepthisCatReStr,
    DVT: keepthisDVTStr,
    management: keepthisManaStr,
    other: keepthisOtherStr,
    exit_site_infection: keepthisInfecStr,
    count_of_follow: thisFollows?.length || 0,
    findFollowTotal,
    any_occlusion,
    any_wo,
    any_co,
    any_rtpa,
    any_infection,
    any_displacement,
    any_limb_edema,
    any_dvt,
    any_breakage,
    any_retained_fb,
    any_limb_ischemia,
    any_nerve_injury,
    any_exit_site_infection,
  };
};

/**
 * Transform a single task with all computed fields
 * ใช้ข้อมูล attempts และ follows ที่ได้จาก API aggregation
 */
export const transformTaskForExport = (task) => {
  const thisAttempts = task?.attempts || [];
  const thisFollows = task?.follows || [];

  // Process attempts และ follows
  const attemptData = processAttempts(thisAttempts);
  const followData = processFollows(thisFollows);

  // Basic task transformations
  const transformedTask = { ...task };

  // Location procedure
  transformedTask["location_porcidure"] = task?.other_location
    ? task?.other_location_name
    : task?.number
    ? "504.5"
    : task?.beside
    ? "Bedside"
    : "";

  // Insertion technique
  transformedTask["insertion_technique"] = task?.fuided_seldinger
    ? "U/S guided, Seldinger"
    : task?.rewire
    ? "rewire"
    : task?.percutaneous
    ? "percutaneous"
    : task?.other_technique
    ? "other_technique_name"
    : "";

  // Confirmation
  transformedTask["confirmation"] =
    task?.iecg && task?.fluoroscopy
      ? "iECG , Fluoroscopy "
      : task?.iecg
      ? "iECG"
      : task?.fluoroscopy
      ? "Fluoroscopy"
      : task?.landmark
      ? "Landmark"
      : "";

  // Patient type
  transformedTask["patient_type"] = task?.other_patient_type_name
    ? task?.other_patient_type_name
    : task?.patient_type?.label || "";

  // Catheter
  transformedTask["catheter"] = task?.other_catheter_name
    ? task?.other_catheter_name
    : task?.catheter?.label || "";

  // Tip of catheter
  transformedTask["tip_of_catheter"] = task?.tip_of_catheter?.label || "";
  transformedTask["tip_of_catheter_lower"] =
    task?.in_ivc && task?.not_in_ivc
      ? ""
      : task?.not_in_ivc
      ? "Not in IVC"
      : task?.in_ivc
      ? "In IVC"
      : "";
  transformedTask["unintended_position"] = task?.other_tip_of_catheter_lower
    ? task?.other_tip_of_catheter_lower_name
    : "";

  // Age
  transformedTask["age"] = task?.dob
    ? Number(ageYearCalculator(task?.dob, new Date(task?.final_date_success)))
    : null;
  transformedTask["age_old"] = task?.dob
    ? ageCalculator(task?.dob, new Date(task?.final_date_success))
    : "";

  // Wards
  transformedTask["ward1"] = task?.option_ward?.label || "";
  transformedTask["ward2"] = task?.ward?.label || task?.other_ward_name || "";

  // Date calculations
  const paper_date_cal =
    task?.paper_date && task?.paper_time
      ? paperDateNpaperTime(task?.paper_date, task?.paper_time)
      : "-";
  const attempt_date_cal =
    task?.attemptsLast?.date && task?.attemptsLast?.out_of_room
      ? paperDateNpaperTime(
          task?.attemptsLast?.date,
          task?.attemptsLast?.out_of_room
        )
      : "-";

  transformedTask["test_1"] = paper_date_cal;
  transformedTask["test_2"] = attempt_date_cal;
  transformedTask["cal_total_wait"] =
    paper_date_cal !== "-" && attempt_date_cal !== "-"
      ? timeDifferenceFull(new Date(attempt_date_cal), new Date(paper_date_cal))
      : "";
  transformedTask["cal_total_wait_hour_min"] =
    paper_date_cal !== "-" && attempt_date_cal !== "-"
      ? timeDiffHourandMinutes(paper_date_cal, attempt_date_cal)
      : "-";
  transformedTask["cal_total_wait_hour"] =
    paper_date_cal !== "-" && attempt_date_cal !== "-"
      ? toNumber(timeDiffHour(paper_date_cal, attempt_date_cal))
      : null;

  // First ward
  const formatFirstWard = () => {
    if (task?.firstLo) {
      if (
        task?.firstLo?.other_current_location_name !== "" &&
        !task?.firstLo?.current_location?.label
      ) {
        return task?.firstLo?.other_current_location_name;
      }
      if (
        task?.firstLo?.other_current_location_name === "" &&
        task?.firstLo?.current_location?.label !== ""
      ) {
        return task?.firstLo?.current_location?.label;
      }
    }
    return "";
  };
  transformedTask["first_ward"] = formatFirstWard();

  // IDs and counts
  transformedTask["id"] = task?._id;
  transformedTask["hn_picc"] = thisAttempts?.length;

  // Side and catheter
  transformedTask["side"] = task?.final_lt ? "Lt" : task?.final_rt ? "Rt" : "";
  transformedTask["catheter_in"] = task?.other_final_catheter_in
    ? task?.other_final_catheter_in
    : task?.final_catheter_in?.label || "";

  // Numeric fields
  transformedTask["use_indwelled"] = toNumber(task?.indwelled);
  transformedTask["exit_site_mark"] = toNumber(task?.exit_site_mark);

  // Status and nurse
  transformedTask["format_status"] = FormatStatus(task?.status);
  transformedTask["use_nurse"] = task?.nurse?.label || "";

  // Dates
  transformedTask["use_final_date"] = task?.final_date_success
    ? new Date(task?.final_date_success)
    : null;
  transformedTask["use_final_date_BE"] = task?.final_date_success
    ? formatDateBE(task?.final_date_success)
    : "";
  // ใช้ task.line_is_no_loanger เป็นหลัก fallback ไปที่ follow ล่าสุด
  // (กัน edge case ที่ task ถูกล้างหลัง programmer ดึงข้อมูลกลับ)
  const removalDate =
    task?.line_is_no_loanger ||
    followData.findFollowTotal?.line_is_no_loanger ||
    "";
  transformedTask["use_done"] = removalDate ? new Date(removalDate) : null;
  transformedTask["use_done_BE"] = removalDate ? formatDateBE(removalDate) : "";
  transformedTask["DOB"] = task?.dob ? new Date(task?.dob) : null;
  transformedTask["DOB_BE"] = task?.dob ? formatDateBE(task?.dob) : "";
  transformedTask["paper_date"] = task?.paper_date
    ? new Date(task?.paper_date)
    : null;
  transformedTask["paper_date_BE"] = task?.paper_date
    ? formatDateBE(task?.paper_date)
    : "";
  transformedTask["paper_time"] = task?.paper_time || "";
  transformedTask["promise_date"] = task?.promise
    ? new Date(task?.promise)
    : null;
  transformedTask["promise_date_BE"] = task?.promise
    ? formatDateBE(task?.promise)
    : "";
  transformedTask["post_cxr"] = task?.doctor?.label || "";

  // Total time
  transformedTask["total_time"] =
    task?.paper_date && task?.final_date_success
      ? calculateHours(
          new Date(task?.paper_date),
          new Date(task?.final_date_success)
        )
      : "-";

  // หน่วย (department)
  const rawDept = task?.user_department || "";
  transformedTask["department_unit"] = rawDept.replace("super_", "");

  // โรคร่วม (Comorbidities) - ใช้ number 1 แทน string "1"
  transformedTask["DM"] = task?.dm ? 1 : "";
  transformedTask["HTN"] = task?.htn ? 1 : "";
  transformedTask["Dyslipidemia"] = task?.dyslipidemia ? 1 : "";
  transformedTask["ESRD"] = task?.esrd ? 1 : "";
  transformedTask["Malignancy"] = task?.mailignancy ? 1 : "";
  transformedTask["CAD"] = task?.cad ? 1 : "";
  transformedTask["GFR30"] = task?.gfr ? 1 : "";
  transformedTask["GFR_detail"] = task?.gfr ? task?.gfr_value : "";
  transformedTask["other_disease"] = task?.other_disease ? 1 : "";
  transformedTask["other_disease_name"] = task?.other_disease
    ? task?.other_disease_name
    : "";

  // Indications - ใช้ number 1 แทน string "1"
  transformedTask["TPN"] = task?.tpn ? 1 : "";
  transformedTask["Prolonged IV meds"] = task?.prolonged_iv_meds ? 1 : "";
  transformedTask["Chemotherapy"] = task?.chemotherapy ? 1 : "";
  transformedTask["Vasopressor"] = task?.vasopressor ? 1 : "";
  transformedTask["เจาะเลือดบ่อย"] = task?.frequent_blood_sampling ? 1 : "";
  transformedTask["Stem cell/ Plasma Exc."] =
    task?.stem_cell_collection_plasma_exchange ? 1 : "";
  transformedTask["Stem cell Tx"] = task?.stem_cell_transplantation ? 1 : "";
  transformedTask["other_indication"] = task?.other_indication ? 1 : "";
  transformedTask["other_indication_name"] = task?.other_indication_name || "";

  // ECG
  transformedTask["ECG"] = task?.ecg?.label || "";
  transformedTask["ECG_NAME"] = task?.other_ecg_name || "";

  // X-ray date
  transformedTask["x_ray_date"] = task?.x_ray_date
    ? new Date(task?.x_ray_date)
    : null;
  transformedTask["x_ray_date_BE"] = task?.x_ray_date
    ? formatDateBE(task?.x_ray_date)
    : "";

  // Weight/Height
  transformedTask["weight"] = toNumber(task?.weight);
  transformedTask["height"] = toNumber(task?.height);
  transformedTask["upper_lower_torso"] = task?.check_torso?.label;
  transformedTask["related_produre"] = task?.other_person;

  // Phone and relation
  const phone = task?.more_patient?.map((row) =>
    row?.phone_number !== "" ? row?.phone_number : ""
  );
  const relation = task?.more_patient?.map((row) =>
    row?.relation !== "" ? row?.relation : ""
  );
  transformedTask["phone_more"] = task?.patient_phone_number
    ? task?.patient_phone_number
    : formatPhone(phone);
  transformedTask["ralation_more"] = task?.involved
    ? task?.involved
    : formatRelation(relation);

  // Attempt data (จาก processAttempts)
  transformedTask["count_of_attempts"] = attemptData.count_of_attempts;
  transformedTask["total_time_room"] = attemptData.total_time_room;
  transformedTask["total_all_time"] = attemptData.total_all_time;
  transformedTask["use_doctor"] = attemptData.use_doctor;
  transformedTask["time"] = attemptData.time;
  transformedTask["complications"] = attemptData.complications;

  // Follow data (จาก processFollows)
  transformedTask["by"] = followData.by;
  transformedTask["line_patency"] = followData.line_patency;
  transformedTask["line_infection"] = followData.line_infection;
  transformedTask["line_displacement"] = followData.line_displacement;
  transformedTask["catheter_related"] = followData.catheter_related;
  transformedTask["DVT"] = followData.DVT;
  transformedTask["management"] = followData.management;
  transformedTask["other"] = followData.other;
  transformedTask["exit_site_infection"] = followData.exit_site_infection;
  transformedTask["count_of_follow"] = followData.count_of_follow;

  // Any_* flags (จาก processFollows)
  transformedTask["any_occlusion"] = followData.any_occlusion;
  transformedTask["any_wo"] = followData.any_wo;
  transformedTask["any_co"] = followData.any_co;
  transformedTask["any_rtpa"] = followData.any_rtpa;
  transformedTask["any_infection"] = followData.any_infection;
  transformedTask["any_displacement"] = followData.any_displacement;
  transformedTask["any_limb_edema"] = followData.any_limb_edema;
  transformedTask["any_dvt"] = followData.any_dvt;
  transformedTask["any_breakage"] = followData.any_breakage;
  transformedTask["any_retained_fb"] = followData.any_retained_fb;
  transformedTask["any_limb_ischemia"] = followData.any_limb_ischemia;
  transformedTask["any_nerve_injury"] = followData.any_nerve_injury;
  transformedTask["any_exit_site_infection"] = followData.any_exit_site_infection;

  // Total catheter day - ใช้ removalDate ตัวเดียวกับ use_done
  let total_cather_day = 0;
  if (removalDate && task?.final_date_success) {
    total_cather_day = timeDifference(
      new Date(`${removalDate}`),
      new Date(`${task?.final_date_success}`)
    );
  }
  transformedTask["totalDay"] = toNumber(total_cather_day);

  // Satisfaction and outcome
  transformedTask["relative_satisfaction"] = toNumber(
    followData.findFollowTotal?.relative_satisfaction
  );
  transformedTask["outcome"] = followData.findFollowTotal?.summary_alive
    ? "A"
    : followData.findFollowTotal?.summary_deceased
    ? "D"
    : "-";
  transformedTask["reason"] =
    followData.findFollowTotal?.other_reasons_for_discontinuation_name ||
    followData.findFollowTotal?.reasons_for_discontinuation?.label ||
    "";
  transformedTask["satisfaction"] = toNumber(
    followData.findFollowTotal?.patient_care_personnel_satisfaction
  );

  // Time catheter
  transformedTask["time_catheter"] =
    task?.paper_date && task?.final_date_success
      ? timeDifference(
          new Date(`${task?.final_date_success}`),
          new Date(`${task?.paper_date}`)
        )
      : "-";

  return transformedTask;
};

/**
 * Inject attempts data into transformed task for Excel export
 */
export const injectAttemptsForExport = (task, maxAttempt) => {
  let sameObj = { ...task };
  const attempts = task?.attempts || [];

  for (let i = 0; i < attempts.length; i++) {
    const attempt = attempts[i];
    sameObj = {
      ...sameObj,
      [`attempt_name_${i + 1}`]: attempt?.attending_name?.label,
      [`use_fellow_${i + 1}`]: attempt?.fellow_name,
      [`use_resident_${i + 1}`]: attempt?.resident_name,
      [`use_operater_${i + 1}`]: attempt?.other_name,
      [`use_side_${i + 1}`]: attempt?.rt ? "Rt" : attempt?.lt ? "Lt" : "",
      [`use_limb_${i + 1}`]: formatLimb(
        attempt?.ue,
        attempt?.le,
        attempt?.neck,
        attempt?.subcalvian,
        attempt?.femoral,
        attempt?.other_position_name
      ),
      [`use_pneumothorax_${i + 1}`]: attempt?.complications_pneumothorax
        ? 1
        : "",
      [`use_hemothorax_${i + 1}`]: attempt?.complications_hemothorax ? 1 : "",
      [`use_arterial_${i + 1}`]: attempt?.complications_arterial_puncture
        ? 1
        : "",
      [`use_paresthesia_${i + 1}`]: attempt?.complications_paresthesia
        ? 1
        : "",
      [`use_nerve_${i + 1}`]: attempt?.complications_nerve_injury ? 1 : "",
      [`use_limbischemia_${i + 1}`]:
        attempt?.complications_limb_ischemia_related_to_arterial_puncture
          ? 1
          : "",
      [`use_other_com_${i + 1}`]: attempt?.other_complications ? 1 : "",
      [`use_other_com_detail_${i + 1}`]:
        attempt?.other_complications_name || "",
      [`use_date_${i + 1}`]: attempt?.date ? new Date(attempt?.date) : null,
      [`use_date_be_${i + 1}`]: attempt?.date
        ? formatDateBE(attempt?.date)
        : "",
      [`attempt_start_${i + 1}`]: attempt?.room_in || "",
      [`attempt_end_${i + 1}`]: attempt?.out_of_room || "",
      [`start_to_end_${i + 1}`]:
        attempt?.room_in && attempt?.out_of_room
          ? diffTime(attempt?.room_in, attempt?.out_of_room)
          : "",
      [`puncture_${i + 1}`]: attempt?.puncture || "",
      [`successful_${i + 1}`]: attempt?.status === "Successful" ? "Y" : "N",
      [`line_finalized_${i + 1}`]: attempt?.line_finalized || "",
      [`punct_to_final_${i + 1}`]:
        attempt?.puncture && attempt?.line_finalized
          ? diffTime(attempt?.puncture, attempt?.line_finalized)
          : "",
      [`procedure_note_${i + 1}`]: attempt?.note || "",
    };
  }

  return sameObj;
};
